import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Building2,
  CheckCircle2,
  Loader2,
  PackageCheck,
  Plus,
  Send,
  ShoppingCart,
  Trash2,
} from 'lucide-react';
import { useState } from 'react';
import { EmptyState } from '../components/EmptyState';
import { HelpHint } from '../components/HelpHint';
import { Modal } from '../components/Modal';
import { PageNav } from '../components/PageNav';
import { api } from '../lib/api';
import { useSession } from '../lib/session';

const STATUS_TONE: Record<string, string> = {
  draft: 'bg-muted text-muted-foreground',
  sent: 'bg-navy-50 text-navy-700',
  part_received: 'bg-amber-50 text-amber-700',
  received: 'bg-emerald-50 text-emerald-700',
  cancelled: 'bg-muted text-muted-foreground line-through',
};

const STATUS_LABEL: Record<string, string> = {
  draft: 'Draft',
  sent: 'Waiting',
  part_received: 'Part delivered',
  received: 'Delivered',
  cancelled: 'Cancelled',
};

/**
 * Buying things, and booking them in.
 *
 * ## A partial delivery is the normal case
 *
 * A supplier sending forty of fifty is routine, and an order that can only be
 * received all-at-once gets received all-at-once regardless of what actually
 * turned up — which puts ten routers into the ledger that are not on the shelf.
 * So the receive form defaults to what is still outstanding and the order stays
 * open until every line is complete.
 */
export function PurchaseOrdersPage() {
  const queryClient = useQueryClient();
  const { can } = useSession();
  const [drafting, setDrafting] = useState(false);
  const [addingSupplier, setAddingSupplier] = useState(false);
  const [receiving, setReceiving] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const orders = useQuery({ queryKey: ['purchase-orders'], queryFn: () => api.purchaseOrders() });
  const suppliers = useQuery({ queryKey: ['suppliers'], queryFn: () => api.suppliers() });

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ['purchase-orders'] });
    await queryClient.invalidateQueries({ queryKey: ['suppliers'] });
    await queryClient.invalidateQueries({ queryKey: ['stock-overview'] });
  };

  const send = useMutation({
    mutationFn: (id: string) => api.sendPurchaseOrder(id),
    onSuccess: async (result) => {
      setNotice(result.message);
      await refresh();
    },
  });

  const rows = orders.data ?? [];
  const owed = (suppliers.data ?? []).reduce((sum, row) => sum + row.owedCents, 0);

  return (
    <div className="space-y-6">
      <PageNav crumbs={[{ label: 'Purchasing' }]} />

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-1.5 font-heading text-2xl">
            Purchasing
            <HelpHint>
              Stock only exists once a delivery is booked in against an order. The person who
              raises an order and the person who confirms it arrived can be different people —
              that is the point of keeping the two apart.
            </HelpHint>
          </h1>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
            {rows.length === 0
              ? 'No orders yet.'
              : `${rows.filter((row) => row.status === 'sent' || row.status === 'part_received').length} waiting on a delivery.`}
            {owed > 0 &&
              ` ${new Intl.NumberFormat('en-KE', { style: 'currency', currency: 'KES', maximumFractionDigits: 0 }).format(owed / 100)} owed to suppliers.`}
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          {can('supplier:write') && (
            <button type="button" className="btn-quiet" onClick={() => setAddingSupplier(true)}>
              <Building2 className="h-4 w-4" aria-hidden="true" />
              Add a supplier
            </button>
          )}
          {can('purchase:write') && (
            <button type="button" className="btn-primary" onClick={() => setDrafting(true)}>
              <ShoppingCart className="h-4 w-4" aria-hidden="true" />
              Raise an order
            </button>
          )}
        </div>
      </div>

      {notice && (
        <p className="flex items-start gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          {notice}
        </p>
      )}

      {/* ── Suppliers ─────────────────────────────────────────────────────── */}
      {(suppliers.data ?? []).length > 0 && (
        <div className="card">
          <h2 className="font-heading text-lg">Suppliers</h2>
          <ul className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {suppliers.data!.map((supplier) => (
              <li key={supplier.id} className="rounded-lg border border-border px-3 py-2 text-sm">
                <p className="font-medium">{supplier.name}</p>
                <p className="text-xs text-muted-foreground">
                  {supplier.owedCents > 0 ? `${supplier.owed} owed` : 'Nothing owed'}
                  {supplier.onOrderCents > 0 && ` · ${supplier.onOrder} on order`}
                </p>
              </li>
            ))}
          </ul>
        </div>
      )}

      {orders.isPending && (
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          Loading the orders
        </p>
      )}

      {orders.data && rows.length === 0 && (
        <EmptyState
          icon={ShoppingCart}
          headline="No purchase orders yet"
          body="Add a supplier, raise an order, and book the delivery in when it arrives. That is what puts stock into the system."
        />
      )}

      {rows.length > 0 && (
        <div className="overflow-x-auto rounded-xl border border-border">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-left text-xs label-mono tracking-[0.12em] text-muted-foreground">
              <tr>
                <th className="px-4 py-3">Order</th>
                <th className="px-4 py-3">Supplier</th>
                <th className="px-4 py-3">Into</th>
                <th className="px-4 py-3 text-right tabular-nums">Total</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id} className="border-t border-border hover:bg-muted/30">
                  <td className="whitespace-nowrap px-4 py-3 font-medium">{row.number}</td>
                  <td className="px-4 py-3">{row.supplierName}</td>
                  <td className="px-4 py-3 text-muted-foreground">{row.locationName}</td>
                  <td className="whitespace-nowrap px-4 py-3 text-right tabular-nums">
                    {row.total}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`badge ${STATUS_TONE[row.status]}`}>
                      {STATUS_LABEL[row.status]}
                      {row.outstandingLines > 0 && row.status === 'part_received'
                        ? ` · ${row.outstandingLines} left`
                        : ''}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    {row.status === 'draft' && can('purchase:write') && (
                      <button
                        type="button"
                        className="btn-quiet text-xs"
                        disabled={send.isPending}
                        onClick={() => send.mutate(row.id)}
                      >
                        {send.isPending && send.variables === row.id ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
                        ) : (
                          <Send className="h-3.5 w-3.5" aria-hidden="true" />
                        )}
                        Mark as sent
                      </button>
                    )}
                    {(row.status === 'sent' || row.status === 'part_received') &&
                      can('purchase:receive') && (
                        <button
                          type="button"
                          className="btn-quiet text-xs"
                          onClick={() => {
                            setReceiving(row.id);
                            setNotice(null);
                          }}
                        >
                          <PackageCheck className="h-3.5 w-3.5" aria-hidden="true" />
                          Book in a delivery
                        </button>
                      )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {addingSupplier && (
        <SupplierDialog
          onClose={() => setAddingSupplier(false)}
          onDone={async (name) => {
            setNotice(`${name} added.`);
            setAddingSupplier(false);
            await refresh();
          }}
        />
      )}

      {drafting && (
        <OrderDialog
          onClose={() => setDrafting(false)}
          onDone={async (message) => {
            setNotice(message);
            setDrafting(false);
            await refresh();
          }}
        />
      )}

      {receiving && (
        <ReceiveDialog
          orderId={receiving}
          onClose={() => setReceiving(null)}
          onDone={async (message) => {
            setNotice(message);
            setReceiving(null);
            await refresh();
          }}
        />
      )}
    </div>
  );
}

function SupplierDialog({
  onClose,
  onDone,
}: {
  onClose: () => void;
  onDone: (name: string) => void;
}) {
  const [name, setName] = useState('');
  const [contactName, setContact] = useState('');
  const [phoneE164, setPhone] = useState('');
  const [kraPin, setKraPin] = useState('');
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () =>
      api.createSupplier({
        name,
        contactName: contactName.trim() || undefined,
        phoneE164: phoneE164.trim() || undefined,
        kraPin: kraPin.trim() || undefined,
      }),
    onSuccess: () => onDone(name),
    onError: (e: Error) => setError(e.message),
  });

  return (
    <Dialog title="Add a supplier" onClose={onClose}>
      <div className="space-y-3">
        <label className="block text-sm">
          Name
          <input className="input mt-1" value={name} onChange={(e) => setName(e.target.value)} />
        </label>
        <div className="flex flex-wrap gap-3">
          <label className="min-w-40 flex-1 text-sm">
            Contact <span className="text-muted-foreground">(optional)</span>
            <input
              className="input mt-1"
              value={contactName}
              onChange={(e) => setContact(e.target.value)}
            />
          </label>
          <label className="min-w-40 flex-1 text-sm">
            Phone <span className="text-muted-foreground">(optional)</span>
            <input
              className="input mt-1"
              value={phoneE164}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="0712345678"
            />
          </label>
        </div>
        <label className="block text-sm">
          KRA PIN <span className="text-muted-foreground">(needed to reclaim input VAT)</span>
          <input
            className="input mt-1"
            value={kraPin}
            onChange={(e) => setKraPin(e.target.value)}
            placeholder="P051234567X"
          />
        </label>
      </div>

      {error && (
        <p className="mt-4 rounded-xl border border-danger-200 bg-danger-50 px-3 py-2 text-sm text-danger-700">
          {error}
        </p>
      )}

      <DialogActions
        onClose={onClose}
        disabled={create.isPending || name.trim().length < 2}
        pending={create.isPending}
        label="Add them"
        onConfirm={() => {
          setError(null);
          create.mutate();
        }}
      />
    </Dialog>
  );
}

type OrderLine = { code: string; quantity: string; unitCost: string };

function OrderDialog({
  onClose,
  onDone,
}: {
  onClose: () => void;
  onDone: (message: string) => void;
}) {
  const [supplierId, setSupplierId] = useState('');
  const [locationId, setLocationId] = useState('');
  const [supplierRef, setSupplierRef] = useState('');
  const [lines, setLines] = useState<OrderLine[]>([{ code: '', quantity: '', unitCost: '' }]);
  const [error, setError] = useState<string | null>(null);

  const suppliers = useQuery({ queryKey: ['suppliers'], queryFn: () => api.suppliers() });
  const locations = useQuery({ queryKey: ['stock-locations'], queryFn: () => api.stockLocations() });
  const catalogue = useQuery({ queryKey: ['stock-catalogue'], queryFn: () => api.stockCatalogue() });

  const stores = (locations.data ?? []).filter((location) => location.kind === 'store');

  const create = useMutation({
    mutationFn: () =>
      api.createPurchaseOrder({
        supplierId,
        locationId: locationId || stores[0]?.id,
        supplierRef: supplierRef.trim() || undefined,
        lines: lines
          .filter((line) => line.code && line.quantity && line.unitCost)
          .map((line) => ({
            code: line.code,
            quantity: Number(line.quantity),
            // Shillings on screen, cents on the wire.
            unitCostCents: Math.round(Number(line.unitCost) * 100),
          })),
      }),
    onSuccess: (result) => onDone(`${result.number} raised for ${result.total}.`),
    onError: (e: Error) => setError(e.message),
  });

  const setLine = (index: number, patch: Partial<OrderLine>) =>
    setLines((current) => current.map((line, i) => (i === index ? { ...line, ...patch } : line)));

  const total = lines.reduce(
    (sum, line) => sum + (Number(line.quantity) || 0) * (Number(line.unitCost) || 0),
    0,
  );

  return (
    <Dialog title="Raise a purchase order" onClose={onClose} wide>
      <div className="flex flex-wrap gap-3">
        <label className="min-w-48 flex-1 text-sm">
          Supplier
          <select
            className="input mt-1"
            value={supplierId}
            onChange={(e) => setSupplierId(e.target.value)}
          >
            <option value="">Choose…</option>
            {(suppliers.data ?? []).map((supplier) => (
              <option key={supplier.id} value={supplier.id}>
                {supplier.name}
              </option>
            ))}
          </select>
        </label>

        <label className="min-w-40 text-sm">
          Deliver into
          <select
            className="input mt-1"
            value={locationId || stores[0]?.id || ''}
            onChange={(e) => setLocationId(e.target.value)}
          >
            {stores.map((store) => (
              <option key={store.id} value={store.id}>
                {store.name}
              </option>
            ))}
          </select>
        </label>

        <label className="min-w-40 text-sm">
          Their reference <span className="text-muted-foreground">(optional)</span>
          <input
            className="input mt-1"
            value={supplierRef}
            onChange={(e) => setSupplierRef(e.target.value)}
          />
        </label>
      </div>

      <div className="mt-4 space-y-2">
        {lines.map((line, index) => (
          <div key={index} className="flex flex-wrap items-end gap-2">
            <label className="min-w-48 flex-1 text-sm">
              {index === 0 && 'Item'}
              <select
                className="input mt-1"
                value={line.code}
                onChange={(e) => setLine(index, { code: e.target.value })}
              >
                <option value="">Choose an item…</option>
                {(catalogue.data ?? []).map((item) => (
                  <option key={item.code} value={item.code}>
                    {item.name}
                  </option>
                ))}
              </select>
            </label>

            <label className="w-24 text-sm">
              {index === 0 && 'Quantity'}
              <input
                className="input mt-1 tabular-nums"
                inputMode="numeric"
                value={line.quantity}
                onChange={(e) => setLine(index, { quantity: e.target.value })}
              />
            </label>

            <label className="w-32 text-sm">
              {index === 0 && 'Unit cost'}
              <input
                className="input mt-1 tabular-nums"
                inputMode="decimal"
                value={line.unitCost}
                onChange={(e) => setLine(index, { unitCost: e.target.value })}
                placeholder="KES"
              />
            </label>

            {lines.length > 1 && (
              <button
                type="button"
                className="rounded-lg p-2 text-muted-foreground hover:bg-muted"
                onClick={() => setLines((current) => current.filter((_, i) => i !== index))}
              >
                <Trash2 className="h-4 w-4" aria-hidden="true" />
                <span className="sr-only">Remove this line</span>
              </button>
            )}
          </div>
        ))}

        <div className="flex items-center justify-between">
          <button
            type="button"
            className="btn-quiet text-xs"
            onClick={() =>
              setLines((current) => [...current, { code: '', quantity: '', unitCost: '' }])
            }
          >
            <Plus className="h-3.5 w-3.5" aria-hidden="true" />
            Another item
          </button>
          <span className="text-sm tabular-nums text-muted-foreground">
            {total > 0 &&
              `${new Intl.NumberFormat('en-KE', { style: 'currency', currency: 'KES' }).format(total)} including VAT`}
          </span>
        </div>
      </div>

      {error && (
        <p className="mt-4 whitespace-pre-line rounded-xl border border-danger-200 bg-danger-50 px-3 py-2 text-sm text-danger-700">
          {error}
        </p>
      )}

      <DialogActions
        onClose={onClose}
        disabled={
          create.isPending ||
          !supplierId ||
          !lines.some((line) => line.code && line.quantity && line.unitCost)
        }
        pending={create.isPending}
        label="Raise it"
        onConfirm={() => {
          setError(null);
          create.mutate();
        }}
      />
    </Dialog>
  );
}

/**
 * Booking a delivery in.
 *
 * Defaults every line to what is still outstanding, because that is what
 * usually arrived — and a form that starts at zero makes the common case the
 * one that takes the most typing.
 */
function ReceiveDialog({
  orderId,
  onClose,
  onDone,
}: {
  orderId: string;
  onClose: () => void;
  onDone: (message: string) => void;
}) {
  const [counts, setCounts] = useState<Record<string, string>>({});
  const [serials, setSerials] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);

  const order = useQuery({
    queryKey: ['purchase-order', orderId],
    queryFn: () => api.purchaseOrder(orderId),
  });

  const receive = useMutation({
    mutationFn: () =>
      api.receivePurchaseOrder(orderId, {
        lines: (order.data?.lines ?? [])
          .map((line) => ({
            code: line.code,
            quantity: Number(counts[line.code] ?? line.outstanding),
            serial: serials[line.code]?.trim() || undefined,
          }))
          .filter((line) => line.quantity > 0),
      }),
    onSuccess: (result) => onDone(result.message),
    onError: (e: Error) => setError(e.message),
  });

  const outstanding = (order.data?.lines ?? []).filter((line) => line.outstanding > 0);

  return (
    <Dialog title={`Book in a delivery — ${order.data?.number ?? ''}`} onClose={onClose} wide>
      {order.isPending && (
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          Loading the order
        </p>
      )}

      {outstanding.length === 0 && order.data && (
        <p className="text-sm text-muted-foreground">
          Everything on this order has already been booked in.
        </p>
      )}

      <div className="space-y-2">
        {outstanding.map((line) => (
          <div key={line.id} className="flex flex-wrap items-end gap-2">
            <span className="min-w-40 flex-1 text-sm">
              {line.name}
              <span className="block text-xs text-muted-foreground">
                {line.outstanding} still expected of {line.quantity}
              </span>
            </span>

            <label className="w-28 text-sm">
              Arrived
              <input
                className="input mt-1 tabular-nums"
                inputMode="decimal"
                value={counts[line.code] ?? String(line.outstanding)}
                onChange={(e) => setCounts((c) => ({ ...c, [line.code]: e.target.value }))}
              />
            </label>

            {line.requiresSerial && (
              <label className="min-w-48 flex-1 text-sm">
                Serial numbers
                <input
                  className="input mt-1"
                  value={serials[line.code] ?? ''}
                  onChange={(e) => setSerials((s) => ({ ...s, [line.code]: e.target.value }))}
                  placeholder="One per unit, comma separated"
                />
              </label>
            )}
          </div>
        ))}
      </div>

      {error && (
        <p className="mt-4 whitespace-pre-line rounded-xl border border-danger-200 bg-danger-50 px-3 py-2 text-sm text-danger-700">
          {error}
        </p>
      )}

      <DialogActions
        onClose={onClose}
        disabled={receive.isPending || outstanding.length === 0}
        pending={receive.isPending}
        label="Book it in"
        onConfirm={() => {
          setError(null);
          receive.mutate();
        }}
      />
    </Dialog>
  );
}

/* ── Small shared pieces ───────────────────────────────────────────────────── */

function Dialog({
  title,
  onClose,
  wide,
  children,
}: {
  title: string;
  onClose: () => void;
  wide?: boolean;
  children: React.ReactNode;
}) {
  /*
   * Delegates to `Modal`, which portals to the root.
   *
   * This drew its own fixed overlay inside the page, and `<main>` is
   * `relative z-10` — a stacking context — so z-50 here was still z-10 to the
   * rest of the page and the footer painted over it. The terms dialog shipped
   * with the same bug and was unusable on the live system.
   */
  return (
    <Modal title={title} onClose={onClose} wide={wide}>
      {children}
    </Modal>
  );
}

function DialogActions({
  onClose,
  onConfirm,
  disabled,
  pending,
  label,
}: {
  onClose: () => void;
  onConfirm: () => void;
  disabled: boolean;
  pending: boolean;
  label: string;
}) {
  return (
    <div className="mt-6 flex items-center justify-end gap-2">
      <button type="button" className="btn-quiet" onClick={onClose}>
        Cancel
      </button>
      <button type="button" className="btn-primary" disabled={disabled} onClick={onConfirm}>
        {pending && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
        {label}
      </button>
    </div>
  );
}
